import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { prop } from 'ramda'
import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { AppPlugins } from 'app/constants'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import useListAction from 'core/hooks/useListAction'
import { IRouterLink } from 'core/plugins/model'
import { createUrlWithQueryString } from 'core/plugins/route'
import { matchesCurrentPath } from 'core/plugins/route-helpers'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import { isKaapiEnabled } from 'core/utils/helpers'
import { userHasRole } from 'core/utils/rbac'
import { listKaapiClusters } from 'k8s/components/kaapi/clusters/actions'
import { kaapiClustersSelector } from 'k8s/components/kaapi/clusters/selectors'
import { hasPermissions } from 'openstack/helpers'
import generateTestId from 'utils/test-helpers'
import NavMenu from '../menu/NavMenu'
import NavItem from './NavItem'

interface Props extends IRouterLink {
  isLastChild?: boolean
  isActive?: boolean
  tooltip?: boolean
  drawerOpen?: boolean
  menuOffset?: { vertical: number; horizontal: number }
  disableMargin?: boolean
  isLeaf?: boolean
  activeDisplayType?: 'background' | 'bar'
  className?: string
  disabled?: boolean
  info?: string
  nestingLevel?: number
  k8sClusterAvailable?: boolean
  meta?: {
    highlight?: boolean
  }
  // Props passed from parent to avoid each NavSection subscribing to route changes
  currentPath?: string
  currentPluginId?: string
}

const processName = (name) => {
  if (!isAdmin()) {
    return name === 'Physical Networks' ? 'Networks' : name
  }
  return name
}

const NavSection = memo(function NavSection({
  name,
  link,
  nestedLinks = undefined,
  icon,
  menuOffset = undefined,
  drawerOpen = false,
  disableMargin = false,
  isLeaf = false,
  isActive = false,
  disabled,
  info,
  nestingLevel = 1,
  k8sClusterAvailable,
  meta,
  isLastChild = false,
  currentPath = '',
  currentPluginId = '',
}: Props) {
  const [open, setOpen] = useState(!!isActive)
  const [menuOpen, setMenuOpen] = useState(false)
  const history = useHistory()
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { virtualNetworking, features } = session
  const filteredNestedLinks = nestedLinks?.filter((link) => {
    const hasRole = link?.requiredRoles ? userHasRole(link.requiredRoles) : true
    const hasBannedRole = link?.bannedRoles ? userHasRole(link.bannedRoles) : false
    const hasRequiredFeatures = link?.requiredFeatures ? link.requiredFeatures(features) : true
    const hasRequiredPermissions = link?.requiredPermissions
      ? link.requiredPermissions.some((permission) => hasPermissions(permission))
      : true
    return hasRole && !hasBannedRole && hasRequiredFeatures && hasRequiredPermissions
  })

  // Close section when navigating away (isActive becomes false)
  // Opening is handled by click handler for instant response
  useEffect(() => {
    if (!isActive) {
      setOpen(false)
    }
  }, [isActive])

  // Close menu popover when route changes (for collapsed sidebar)
  useEffect(() => {
    if (menuOpen) {
      setMenuOpen(false)
    }
  }, [currentPath])

  // Cleanup pending navigation on unmount
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current)
      }
    }
  }, [])

  const handleMenuOpen = () => {
    if (drawerOpen || !filteredNestedLinks?.length) return
    setMenuOpen(true)
  }

  // Get cluster parameter for URL replacement
  const { prefs: k8sGlobalPrefs } = useScopedPreferences('k8sPluginGlobalParams')
  const { reload: reloadClusters } = useListAction(listKaapiClusters, { loadOnDemand: true })
  const clusterList = useSelector(kaapiClustersSelector)
  const clusterParam = k8sGlobalPrefs?.cluster || clusterList?.[0]?.metadata?.name || ''

  useEffect(() => {
    if (isKaapiEnabled()) {
      reloadClusters(true)
    }
  }, [])

  const onNavItemClick = (event) => {
    // prevent from triggering click for nav item when the drawer is closed.
    // This is to prevent the nav item url from changing when the drawer is closed
    if (!filteredNestedLinks?.length) {
      return
    }

    if (!drawerOpen) {
      event.preventDefault()
      return
    }

    // Prevent default navigation - we'll handle it manually
    event.preventDefault()

    // Toggle immediately for instant animation
    setOpen((prev) => !prev)

    // Navigate after a short delay to let animation start
    const targetPath = link?.path
    if (targetPath) {
      navigationTimeoutRef.current = setTimeout(() => {
        // Replace URL parameters before navigation
        const resolvedPath =
          (targetPath.includes('/kubernetes') || targetPath.includes('/clusters')) &&
          !targetPath.includes('/infrastructure/clusters')
            ? createUrlWithQueryString(targetPath, { cluster: clusterParam })
            : targetPath
        history.push(resolvedPath)
        navigationTimeoutRef.current = null
      }, 0)
    }
  }

  const innerNested = nestingLevel === 2
  const isAccountPlugin = currentPluginId === AppPlugins.MyAccount
  const classes = useStyles()

  const processedName = processName(name)

  const navSection = (
    <div
      className={clsx(classes.navHeader, {
        [classes.navHeaderActive]: isActive,
        [classes.navHeaderInnerNested]: innerNested,
      })}
      onClick={drawerOpen ? undefined : handleMenuOpen}
    >
      <NavItem
        topLevel={!innerNested}
        name={processedName}
        link={link}
        isActive={isActive}
        highlight={meta?.highlight}
        drawerOpen={drawerOpen}
        tooltip={info}
        icon={icon}
        onClick={onNavItemClick}
        variant="sidenav1"
        tooltipProps={{
          className: clsx(classes.navItemContainer, {
            [classes.navItemContainerInnerNested]: innerNested,
            [classes.navItemContainerActive]: isActive,
            [classes.navItemContainerHighlight]: meta?.highlight,
          }),
        }}
        disabled={disabled}
        isLastChild={isLastChild}
        currentPluginId={currentPluginId}
        isLeaf={isLeaf}
        compact={innerNested}
      />
      {filteredNestedLinks?.length && (
        <FontAwesomeIcon
          solid
          size="xs"
          className={clsx(classes.navIcon, { [classes.navIconVisible]: drawerOpen })}
          onClick={() => setOpen(!open)}
        >
          {innerNested ? (open ? 'minus' : 'plus') : open ? 'chevron-up' : 'chevron-down'}
        </FontAwesomeIcon>
      )}
    </div>
  )

  return (
    <div
      className={classes.navSectionContainer}
      style={{ marginBottom: drawerOpen && !disableMargin ? 8 : 0 }}
    >
      {innerNested ? (
        navSection
      ) : (
        <NavMenu
          title={name}
          icon={icon}
          data-testid={generateTestId('nav-section', 'menu')}
          nestedLinks={filteredNestedLinks}
          onClose={() => setMenuOpen(false)}
          k8sClusterAvailable={k8sClusterAvailable}
          info={info}
          open={menuOpen}
          offset={menuOffset}
          origin="left top"
          anchor={navSection}
          disabled={disabled}
          drawerOpen={drawerOpen}
          currentPath={currentPath}
          currentPluginId={currentPluginId}
        />
      )}
      {filteredNestedLinks?.length && (
        <div
          onClick={(event) => event.stopPropagation()}
          className={clsx(classes.nestedLinks, {
            [classes.nestedLinksOpen]: drawerOpen && open,
            [classes.nestedLinksInnerNested]: innerNested,
            [classes.nestedLinksLastChild]: isLastChild,
            [classes.nestedLinksHighlight]: !!meta?.highlight,
          })}
        >
          <div
            className={classes.nestedLinksLineWrapper}
            style={{ marginLeft: innerNested ? 24 : '36px' }}
          >
            {filteredNestedLinks.map((navItem, index) => {
              const {
                link: nestedLink,
                name: nestedLinkName,
                disabledCond,
                meta,
                tooltipFn,
                isHidden,
                nestedLinks,
                isk8sNav,
                isBetaVersion,
                betaVersionTooltip,
              } = navItem
              const name = processName(nestedLinkName)
              const disableK8sNavs = isk8sNav && !k8sClusterAvailable
              const isLastChild = filteredNestedLinks[filteredNestedLinks.length - 1] === navItem

              if (nestedLinks) {
                return (
                  <NavSection
                    {...navItem}
                    key={`${name}-${index}`}
                    isActive={!!matchesCurrentPath(currentPath, navItem)}
                    isLastChild={isLastChild}
                    drawerOpen={drawerOpen}
                    nestingLevel={2}
                    disabled={disableK8sNavs}
                    info={info ? info : disableK8sNavs ? 'No Clusters Available' : ''}
                    currentPath={currentPath}
                    currentPluginId={currentPluginId}
                    disableMargin
                  />
                )
              }

              return isHidden && isHidden({ virtualNetworking }) ? null : (
                <NavItem
                  isLeaf
                  key={index}
                  name={name}
                  link={nestedLink}
                  activeDisplayType="bar"
                  className={classes.nestedNavItem}
                  isActive={!!matchesCurrentPath(currentPath, navItem)}
                  highlight={meta?.highlight}
                  isLastChild={isLastChild}
                  drawerOpen={drawerOpen}
                  tooltip={tooltipFn ? tooltipFn({ virtualNetworking }) : info}
                  variant="sidenav2"
                  tooltipProps={{
                    className: clsx(classes.nestNavContainer, {
                      [classes.nestNavContainerActive]: isActive,
                    }),
                  }}
                  disabled={disabledCond ? disabledCond({ virtualNetworking }) : false || disabled}
                  isBetaVersion={isBetaVersion}
                  betaVersionTooltip={betaVersionTooltip}
                  currentPluginId={currentPluginId}
                />
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
})

export default NavSection

const useStyles = makeStyles<Theme>((theme) => ({
  navSectionContainer: {
    // marginBottom set via inline style
  },
  navHeader: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    transform: 'translateZ(0)',
    willChange: 'background-color',
    '&:hover': {
      transition: 'background-color 0.2s ease',
      background: theme.components.sidebar.border,
    },
  },
  navHeaderActive: {
    '&:hover': {
      background: 'unset',
    },
  },
  navHeaderInnerNested: {
    '&:hover': {
      background: 'unset',
    },
  },
  navIcon: {
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 24,
    position: 'absolute',
    right: theme.spacing(2),
    top: '50%',
    transform: 'translateY(-50%)',
    color: theme.palette.grey[700],
    transition: 'color 0.2s ease',
    '&:hover': {
      color: theme.components.sidebar.hoverText,
    },
  },
  navIconVisible: {
    display: 'flex',
  },
  navItemContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    marginLeft: 23,
    marginTop: 0,
    color: theme.components.sidebar?.text,
  },
  navItemContainerInnerNested: {
    marginLeft: 10,
    marginTop: 12,
  },
  navItemContainerActive: {
    color: theme.components.sidebar?.activeText,
  },
  nestNavContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    color: theme.components.sidebar?.text,
    marginTop: 9,
    paddingLeft: theme.spacing(1),
  },
  nestNavContainerActive: {
    color: theme.components.sidebar?.activeText,
  },
  nestedNavItem: {
    '&:hover .nav-text': {
      transition: 'color 0.2s ease',
      color: theme.palette.grey[700],
    },
  },
  nestedLinks: {
    display: 'grid',
    gridTemplateRows: '0fr',
    transition: 'grid-template-rows 0.35s cubic-bezier(0.04, 0.62, 0.23, 0.98)',
    willChange: 'grid-template-rows',
    contain: 'layout style paint',
    position: 'relative',
    '&:before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: 0,
      background: theme.palette.grey[200],
    },
  },
  nestedLinksInnerNested: {
    '&:before': {
      width: '1px',
    },
  },
  nestedLinksLastChild: {
    '&:before': {
      width: 0,
    },
  },
  nestedLinksHighlight: {
    '&:before': {
      background: theme.palette.blue[400],
    },
  },
  nestedLinksOpen: {
    gridTemplateRows: '1fr',
  },
  nestedLinksLineWrapper: {
    // marginLeft set via inline style
    overflow: 'hidden',
    minHeight: 0,
    transform: 'translateZ(0)',
    backfaceVisibility: 'hidden',
  },
}))
